import { REJECT, array, nullable, num, object, str, type FieldParser, type Received } from 'validation';

import { getRecord } from 'api/shared/getRecord';

// Source of truth for the wire shape: nap-backend `routes/workflows.js` GET /workflows/:workflow_id, with each action's
// `dropdown_parameters` attached by `getWorkflowActions` from `node_instance_parameters`. The route also returns the
// workflow's parameters, stages and current stage, and per action its files, change details and upload flag; the inbox
// detail panel reads none of those. This projection declares what the panel renders and what it sends back when the
// operator responds or saves, so the panel can refuse a record it cannot act on without being refused over the rest.
//
// The text columns are nullable in `workflows` / `workflow_actions` and are copied from node configuration, so they are
// declared nullable. The timestamps are nullable columns too, but default to now() and are never written explicitly.

/** An answer the operator can give: a node definition handle, so `id` is a uuid and the thing `respond` sends back. */
export interface InboxWorkflowResponseOption {
  readonly id: string;
  readonly label: string;
}

/** A free-text field the operator fills in before responding. */
export interface InboxWorkflowInputOption {
  readonly label: string;
}

/** A saved value of one of those fields. */
export interface InboxWorkflowInput {
  readonly label: string;
  readonly value: string;
}

/**
 * The answer given. Written by `PUT /workflows/respond/:id` as the chosen option plus `user` and `timestamp`, and by a
 * cancelled node instance as a bare label with a nullable user.
 */
export interface InboxWorkflowResponse {
  readonly label: string;
  readonly user: string | null;
  readonly timestamp: number;
}

/** A `Dropdown` / `KeyValueDropdown` parameter of the action's node instance, which the operator may change. */
export interface InboxWorkflowDropdownParameter {
  readonly parameter_id: number;
  readonly name: string;
  readonly default_expression: string | null;
  readonly value: string | null;
  readonly submit_value: string | null;
  readonly out_value: string | null;
  /** Comma-separated choices. */
  readonly options: string | null;
}

export interface InboxWorkflowAction {
  readonly workflow_action_id: number;
  readonly subject: string | null;
  readonly message: string | null;
  readonly created_at: string;
  readonly response: InboxWorkflowResponse | null;
  readonly response_options: readonly InboxWorkflowResponseOption[];
  readonly response_input_options: readonly InboxWorkflowInputOption[];
  readonly response_inputs: readonly InboxWorkflowInput[];
  readonly dropdown_parameters: readonly InboxWorkflowDropdownParameter[];
}

export interface InboxWorkflow {
  readonly workflow_id: number;
  readonly workflow_subject: string | null;
  readonly workflow_category: string | null;
  readonly created_at: string;
  readonly updated_at: string;
  readonly workflows_actions: readonly InboxWorkflowAction[];
}

const inputSchema = object<InboxWorkflowInput>({ label: str(), value: str() });

const isEmptyObject = (raw: unknown): boolean => typeof raw === 'object' && raw !== null && !Array.isArray(raw) && Object.keys(raw).length === 0;

/**
 * NOTE: an action whose inputs were never saved has a NULL column, which the route sends as `{}` (`response_inputs ||
 * {}`). That is the wire's spelling of "none", not drift, so it reads as an empty list; any other non-array still drifts.
 */
const inputsSchema: FieldParser<InboxWorkflowInput[]> = (raw, path, warnings) =>
  isEmptyObject(raw) ? { ok: true, value: [] } : array(inputSchema)(raw, path, warnings);

const actionSchema = object<InboxWorkflowAction>({
  workflow_action_id: num(REJECT),
  subject: nullable(str()),
  message: nullable(str()),
  created_at: str(),
  response: nullable(object<InboxWorkflowResponse>({ label: str(), user: nullable(str()), timestamp: num() })),
  response_options: array(object<InboxWorkflowResponseOption>({ id: str(REJECT), label: str() })),
  response_input_options: array(object<InboxWorkflowInputOption>({ label: str() })),
  response_inputs: inputsSchema,
  dropdown_parameters: array(
    object<InboxWorkflowDropdownParameter>({
      parameter_id: num(REJECT),
      name: str(),
      default_expression: nullable(str()),
      value: nullable(str()),
      submit_value: nullable(str()),
      out_value: nullable(str()),
      options: nullable(str()),
    })
  ),
});

const workflowSchema = object<InboxWorkflow>({
  workflow_id: num(REJECT),
  workflow_subject: nullable(str()),
  workflow_category: nullable(str()),
  created_at: str(),
  updated_at: str(),
  workflows_actions: array(actionSchema),
});

/**
 * GET `/workflows/:workflow_id` for the inbox detail panel, which reads its workflow by id rather than out of the paged
 * sidebar list, so a deep link to a workflow beyond the loaded pages still shows. See `getRecord` for the outcomes:
 * `null` is the server saying there is no such workflow.
 */
export const getInboxWorkflow = (id: number): Promise<Received<InboxWorkflow | null>> =>
  getRecord(`/workflows/${id}`, { schema: workflowSchema, source: 'getInboxWorkflow' });
